import {useMemo, useState} from 'react';
import {
  Avatar,
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchIcon from '@mui/icons-material/Search';
import {GridColDef} from '@mui/x-data-grid';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {Building} from 'src/apis/autoGenerate/types/building';
import {Tenant} from 'src/apis/autoGenerate/types/tenant';
import {EntioDataGrid, StatusBadge} from 'src/components/ui';
import {ReactComponent as SIPCallIcon} from 'src/assets/SIP_Call.svg';
import {ReactComponent as MobileAppIcon} from 'src/assets/Mobile_App.svg';
import {brand} from 'src/theme';
import {getResidentInitials, getResidentName, isResidentOnline} from './types';

interface ResidentsTabProps {
  tenants: Tenant[];
  apartments: Apartment[];
  buildings: Building[];
  propertyId: string;
  buildingId: string;
  onPropertyChange: (propertyId: string) => void;
  onBuildingChange: (buildingId: string) => void;
  onReset: () => void;
  loading: boolean;
  callingTenantId?: string;
  onCall: (tenant: Tenant) => void;
}

export default function ResidentsTab({
  tenants,
  apartments,
  buildings,
  propertyId,
  buildingId,
  onPropertyChange,
  onBuildingChange,
  onReset,
  loading,
  callingTenantId,
  onCall,
}: ResidentsTabProps) {
  const [search, setSearch] = useState('');

  const buildingsForFilter = useMemo(() => {
    if (propertyId === 'all') return buildings;
    return buildings.filter(building => building.apartmentId === propertyId);
  }, [buildings, propertyId]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tenants.filter(tenant => {
      if (propertyId !== 'all' && tenant.apartment?.id !== propertyId) return false;
      if (buildingId !== 'all' && tenant.building?.id !== buildingId) return false;
      if (!query) return true;
      const haystack = [
        tenant.firstName,
        tenant.lastName,
        tenant.room?.name,
        tenant.mobile,
        tenant.user?.mobile,
        tenant.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [buildingId, propertyId, search, tenants]);

  const handleReset = () => {
    setSearch('');
    onReset();
  };

  const columns: GridColDef<Tenant>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 180,
      valueGetter: params => getResidentName(params.row),
      renderCell: params => (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
          <Avatar sx={{width: 32, height: 32, bgcolor: brand[500], fontSize: 12, fontWeight: 600}}>
            {getResidentInitials(params.row)}
          </Avatar>
          <Typography variant="body2">{getResidentName(params.row)}</Typography>
        </Box>
      ),
    },
    {
      field: 'unit',
      headerName: 'Unit',
      flex: 0.6,
      minWidth: 80,
      valueGetter: params => params.row.room?.name || '—',
    },
    {
      field: 'building',
      headerName: 'Building',
      flex: 0.8,
      minWidth: 110,
      valueGetter: params => params.row.building?.name || '—',
    },
    {
      field: 'callMethod',
      headerName: 'Call Method',
      flex: 0.8,
      minWidth: 120,
      renderCell: params => {
        const callMethod = params.row.user?.callMethod || 'MOBILE_APP';
        const label = callMethod === 'SIP_PHONE' ? 'SIP Phone' : 'App';
        const Icon = callMethod === 'SIP_PHONE' ? SIPCallIcon : MobileAppIcon;
        return (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Icon style={{width: 16, height: 16}} />
            <Typography variant="body2">{label}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 90,
      renderCell: params => {
        const online = isResidentOnline(params.row);
        return <StatusBadge status={online ? 'success' : 'neutral'} label={online ? 'Online' : 'Offline'} />;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.7,
      minWidth: 110,
      sortable: false,
      renderCell: params => (
        <Button
          variant="contained"
          size="small"
          startIcon={<PhoneIcon />}
          disabled={callingTenantId === params.row.id}
          onClick={() => onCall(params.row)}
        >
          Call
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap'}}>
        <OutlinedInput
          size="small"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search residents by name, unit, or phone..."
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          }
          sx={{flex: 1, minWidth: 240, bgcolor: 'background.paper'}}
        />
        <FormControl size="small" sx={{minWidth: 160}}>
          <Select
            value={propertyId}
            onChange={event => onPropertyChange(event.target.value)}
          >
            <MenuItem value="all">All Properties</MenuItem>
            {apartments.map(apartment => (
              <MenuItem key={apartment.id} value={apartment.id}>
                {apartment.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{minWidth: 160}}>
          <Select value={buildingId} onChange={event => onBuildingChange(event.target.value)}>
            <MenuItem value="all">All Buildings</MenuItem>
            {buildingsForFilter.map(building => (
              <MenuItem key={building.id} value={building.id}>
                {building.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
      </Box>

      <Typography variant="subtitle1" sx={{mb: 1.5}}>
        Residents ({filtered.length})
      </Typography>

      <EntioDataGrid
        rows={filtered}
        columns={columns}
        loading={loading}
        getRowId={row => row.id || `${row.email}-${row.roomId}`}
        emptyMessage="No residents"
        emptyDescription="No residents match the current search or filters."
      />
    </Box>
  );
}
